/**
 * RabbitMQ WebSocket Service using raw WebSocket with STOMP protocol
 */

import { GpsDeniedMessageResponse } from '../../modules/customers/models/RabbitMQ';
import { QUEUE_NAME } from '../common/constants';
import { RABBITMQ_CONFIG } from '../common/constants/RabbitMQConfig';

export type RabbitMQMessageType = 'gps_denied' | 'trip_queue';

// Queue for messages that arrive before handlers are registered
interface PendingMessage {
  message: RabbitMQMessage;
  messageId: string;
  subscriptionId: string;
}
const pendingMessages: PendingMessage[] = [];

export interface TripStatusMessage {
  ticketId: string;
  tripId: string;
  stopType: string;
  status: string,
  ticketWorkerStatus: string;
  userId: string,
  workerId: string;
  appVersion: string;
  event: string;
  eventData: any
}
export interface RabbitMQMessage {
  type: RabbitMQMessageType;
  data: GpsDeniedMessageResponse;
  customerPhone?: string;
  customerId?: string;
}

export type MessageHandler = (message: RabbitMQMessage) => void;

class RabbitMQWebSocketService {
  private ws: WebSocket | null = null;
  private isAuthenticated = false;
  private isConnecting = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 3000;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private heartbeatTimer: NodeJS.Timeout | null = null;
  private messageHandlers: Map<RabbitMQMessageType, Set<MessageHandler>> = new Map();
  private customerPhone: string | null = null;
  private currentWsUrl = '';
  private hasConnectedOnce = false;
  private stopAutoReconnectForCurrentSession = false;
  /** Tránh gọi connect() song song cùng SĐT (Strict Mode / effect lặp) */
  private activeConnectPromise: Promise<void> | null = null;
  private activeConnectPhone: string | null = null;
  private subscriptionMeta: Map<
    string,
    {
      destination: string;
      type: RabbitMQMessageType;
    }
  > = new Map();

  private subscriptions = new Map<
    string,
    {
      subscriptionId: string;
      refCount: number;
    }
  >();

  /**
   * Get queue name for a specific customer phone
   */
  private getQueueName(customerPhone: string): string {
    // Clean phone number - remove any non-numeric characters
    const cleanPhone = customerPhone.replace(/\D/g, '');
    const queueName = `RabbitMQLocationAccessDeniedQueue_${cleanPhone}`;

    return queueName;
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat() {
    this.stopHeartbeat();

    // Send heartbeat every 8 seconds (less than 10 second interval)
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        // Send heartbeat frame (just a newline)
        this.ws.send('\n');
      }
    }, 8000);
  }

  /**
   * Stop heartbeat timer
   */
  private stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  /**
   * Send STOMP command
   */
  private sendStompCommand(command: string, headers: Record<string, string> = {}, body: string = '') {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn(`❌ Cannot send ${command}: WebSocket not open`);
      return;
    }

    let message = `${command}\n`;
    Object.entries(headers).forEach(([key, value]) => {
      message += `${key}:${value}\n`;
    });
    message += `\n${body}\0`;

    this.ws.send(message);
  }

  /**
   * Parse STOMP frame
   */
  private parseStompFrame(data: string): { command: string; headers: Record<string, string>; body: string } | null {
    const lines = data.split('\n');
    if (lines.length < 1) return null;

    const command = lines[0];
    const headers: Record<string, string> = {};
    let bodyStartIndex = 1;

    // Parse headers
    for (let i = 1; i < lines.length; i++) {
      if (lines[i] === '') {
        bodyStartIndex = i + 1;
        break;
      }
      const [key, ...valueParts] = lines[i].split(':');
      if (key && valueParts.length > 0) {
        headers[key] = valueParts.join(':');
      }
    }

    let body = lines.slice(bodyStartIndex).join('\n');

    const nullCharIndex = body.indexOf('\0');
    if (nullCharIndex !== -1) {
      body = body.substring(0, nullCharIndex);
    }
    body = body.trim();

    return { command, headers, body };
  }

  /**
   * Connect to RabbitMQ WebSocket using STOMP
   */
  connect(customerPhone: string = ""): Promise<void> {
    // If already connected, just update subscription if needed
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isAuthenticated) {
      if (customerPhone) {
        this.customerPhone = customerPhone;
      }

      console.log('✅ RabbitMQ already connected');

      return Promise.resolve();
    }

    if (this.isConnecting && this.activeConnectPromise && this.activeConnectPhone === customerPhone) {
      return this.activeConnectPromise;
    }

    const promise = new Promise<void>((resolve, reject) => {

      this.isConnecting = true;
      this.customerPhone = customerPhone;
      this.stopAutoReconnectForCurrentSession = false;

      try {
        const candidateUrls = RABBITMQ_CONFIG.WS_URLS && RABBITMQ_CONFIG.WS_URLS.length > 0
          ? RABBITMQ_CONFIG.WS_URLS
          : [RABBITMQ_CONFIG.WS_URL];

        const tryConnect = (candidateIndex: number) => {
          if (candidateIndex >= candidateUrls.length) {
            this.isConnecting = false;
            this.isAuthenticated = false;
            this.stopAutoReconnectForCurrentSession = true;
            reject(new Error('WebSocket connection failed'));
            return;
          }

          const wsUrl = candidateUrls[candidateIndex];
          this.currentWsUrl = wsUrl;
          let movedToNextCandidate = false;
          let resolved = false;

          console.log(`🔌 Connecting to RabbitMQ WebSocket: ${wsUrl}`);
          if (wsUrl.startsWith('wss://')) {
            console.warn('Nếu kết nối thất bại, có thể do SSL hoặc route WebSocket trên domain chưa đúng.');
          }

          this.ws = new WebSocket(wsUrl);

          const moveToNextCandidate = () => {
            if (movedToNextCandidate || this.hasConnectedOnce) return;
            movedToNextCandidate = true;

            if (this.ws) {
              this.ws.onopen = null;
              this.ws.onmessage = null;
              this.ws.onerror = null;
              this.ws.onclose = null;
              try {
                this.ws.close();
              } catch (e) {
                // ignore close errors during failover
              }
              this.ws = null;
            }

            tryConnect(candidateIndex + 1);
          };

          this.ws.onopen = () => {
            console.log('🔌 WebSocket connected, sending STOMP CONNECT...');

            // Send CONNECT frame with heartbeat
            this.sendStompCommand('CONNECT', {
              'accept-version': '1.0,1.1,1.2',
              'host': RABBITMQ_CONFIG.VHOST,
              'login': RABBITMQ_CONFIG.USERNAME,
              'passcode': RABBITMQ_CONFIG.PASSWORD,
              'heart-beat': '10000,10000', // outgoing,incoming - reduced to 10 seconds
            });
          };

          this.ws.onmessage = (event) => {
            const data = typeof event.data === 'string' ? event.data : event.data.toString();
            const frame = this.parseStompFrame(data);

            if (!frame) return;

            if (frame.command === 'CONNECTED') {
              console.log('✅ STOMP authenticated successfully');
              this.isConnecting = false;
              this.isAuthenticated = true;
              this.hasConnectedOnce = true;
              this.reconnectAttempts = 0;
              this.reconnectDelay = 3000;
              resolved = true;

              // Start heartbeat to keep connection alive
              this.startHeartbeat();

              // Subscribe to queue after authentication
              // this.subscribe(customerPhone);

              // Subscribe to update status trip queue after authentication
              // this.subscribeUpdateStatusTrip();
              resolve();

            } else if (frame.command === 'MESSAGE') {
              this.handleMessageFrame(frame);

            } else if (frame.command === 'ERROR') {
              console.error('❌ STOMP error:', frame.body, frame.headers);

              // Check if it's a subscription cancellation error
              if (frame.body && frame.body.includes('subscription')) {
                console.warn('⚠️ Subscription cancelled by server, attempting to resubscribe...');
                // Try to resubscribe after a short delay
                setTimeout(() => {
                  if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isAuthenticated) return;
                  this.subscriptions.forEach((sub, destination) => {

                    this.sendStompCommand("SUBSCRIBE", {
                      id: sub.subscriptionId,
                      destination,
                      ack: "client",
                    });

                    console.log("♻️ Resubscribed:", destination);
                  });
                }, 1000);
                return; // Don't fail the connection for subscription errors
              }

              // Check for permission/access errors
              if (frame.body && (frame.body.includes('access') || frame.body.includes('permission'))) {
                console.error('🚫 Permission error - check RabbitMQ user permissions');
                console.error('🔍 Required permissions: read,write,configure for queue');
              }

              this.isConnecting = false;
              this.isAuthenticated = false;
              if (!resolved) {
                reject(new Error(frame.body || 'STOMP error'));
              }

            } else if (frame.command === 'RECEIPT') {
              console.log('✅ STOMP RECEIPT received - command confirmed:', frame.headers);

            } else {
              console.log('📨 Unknown STOMP frame:', frame.command, frame.headers);
            }
          };

          this.ws.onerror = (error) => {
            console.error('❌ WebSocket error:', error);

            if (!this.hasConnectedOnce && candidateIndex < candidateUrls.length - 1) {
              console.warn(`⚠️ WebSocket candidate failed, trying next URL (${candidateIndex + 2}/${candidateUrls.length})`);
              moveToNextCandidate();
              return;
            }

            if (wsUrl.startsWith('wss://')) {
              console.error('🔒 WSS connection failed. Có thể do SSL hoặc route/gateway chưa hỗ trợ WebSocket.');
              console.error(`   URL hiện tại: ${wsUrl}`);
            } else {
              console.error(`❌ WS connection failed at URL: ${wsUrl}`);
            }

            this.isConnecting = false;
            this.isAuthenticated = false;
            this.stopAutoReconnectForCurrentSession = true;
            if (!resolved) {
              reject(new Error('WebSocket connection failed'));
            }
          };

          this.ws.onclose = (event) => {
            const closeCode = event.code;
            const closeReason = event.reason || '';

            if (!this.hasConnectedOnce && candidateIndex < candidateUrls.length - 1) {
              console.warn(
                `⚠️ WebSocket closed before authentication (code ${closeCode}). Trying next URL (${candidateIndex + 2}/${candidateUrls.length})`
              );
              moveToNextCandidate();
              return;
            }

            console.log(`🔌 WebSocket disconnected (Code: ${closeCode}, Reason: ${closeReason || 'None'})`);

            // Stop heartbeat
            this.stopHeartbeat();

            if (closeCode === 1006 || closeCode === 1015 || closeReason.includes('SSL') || closeReason.includes('TLS')) {
              console.error('🔒 WebSocket handshake failed (có thể SSL hoặc websocket route).');
              console.error(`   Close Code: ${closeCode}`);
              console.error(`   Reason: ${closeReason || 'No reason provided'}`);
              console.error(`   URL: ${this.currentWsUrl}`);
            } else if (closeCode === 1005) {
              console.warn('⚠️ WebSocket closed without status code - possible server-side issue or message processing problem');
              console.warn('💡 Kiểm tra:');
              console.warn('   - RabbitMQ server có đang hoạt động bình thường?');
              console.warn('   - Message queue có bị đầy không?');
              console.warn('   - Connection timeout settings');
            }

            this.isConnecting = false;
            this.isAuthenticated = false;
            this.ws = null;
            // this.subscriptionId = null;

            // Auto reconnect if not manually disconnected and not SSL handshake hard-stop
            if (this.stopAutoReconnectForCurrentSession) {
              console.warn('⏹️ Stop auto reconnect: all websocket candidates failed. Chờ lần trigger tiếp theo từ người dùng.');
            } else if (this.customerPhone && this.reconnectAttempts < this.maxReconnectAttempts && closeCode !== 1015) {
              this.scheduleReconnect();
            } else if (closeCode === 1015) {
              console.error('🚫 Không tự động kết nối lại do TLS handshake failure.');
            }
          }
        };

        tryConnect(0);

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });

    this.activeConnectPromise = promise;
    this.activeConnectPhone = customerPhone;

    promise.finally(() => {
      if (this.activeConnectPromise === promise) {
        this.activeConnectPromise = null;
        this.activeConnectPhone = null;
      }
    });

    return promise;
  }

  // /**
  //  * Subscribe to queue for a specific customer phone
  //  */
  public subscribeQueue(queueName: string = "", customerPhone: string = ""): string | null {
    console.log("queueName", queueName);

    queueName = queueName || this.getQueueName(customerPhone)
    if (!this.ws || !this.isAuthenticated) {
      return null;
    }

    const destination = `/queue/${queueName}`
    console.log("destination", destination);
    const existing = this.subscriptions.get(destination);

    if (existing) {
      existing.refCount++;

      console.log(
        `📌 Queue already subscribed: ${destination} (${existing.refCount})`
      );

      return existing.subscriptionId;
    }

    const subscriptionId =
      `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    this.sendStompCommand('SUBSCRIBE', {
      id: subscriptionId,
      destination,
      ack: 'client',
      receipt: `receipt-${subscriptionId}`,
    });

    this.subscriptions.set(destination, {
      subscriptionId,
      refCount: 1,
    });

    this.subscriptionMeta.set(subscriptionId, {
      destination,
      type: destination.includes("LocationAccessDenied")
        ? "gps_denied"
        : "trip_queue",
    });

    console.log(`✅ Subscribed: ${destination}`);

    return subscriptionId;
  }
  /**
   * Handle incoming MESSAGE frame
   */
  private handleMessageFrame(frame: { command: string; headers: Record<string, string>; body: string }) {
    const messageId = frame.headers['message-id'];
    const subscriptionId = frame.headers['subscription']
    if (!messageId) {
      console.error('❌ Received MESSAGE frame without message-id');
      return;
    }

    const meta = this.subscriptionMeta.get(subscriptionId);

    if (!meta) {
      console.warn("⚠️ Unknown subscriptionId:", subscriptionId);
      return;
    }

    console.log(`📨 Processing message ${messageId} from subscription ${subscriptionId}`);

    try {
      const messageData: any = JSON.parse(frame.body);

      let rabbitMQMessage: RabbitMQMessage;

      if (meta.type === "trip_queue") {
        rabbitMQMessage = {
          type: "trip_queue",
          data: messageData,
        };
      } else {
        rabbitMQMessage = {
          type: "gps_denied",
          data: {
            customerId: messageData.CustomerId,
            customerPhone: messageData.CustomerPhone,
          },
        };
      }

      // Handle message asynchronously - wait for all handlers to complete before ACK
      this.handleMessageAsync(rabbitMQMessage, messageId, subscriptionId || undefined).then((shouldAck) => {
        if (shouldAck) {
          console.log(`✅ ACKing message ${messageId}`);
          this.ackMessage(messageId, subscriptionId);
        } else {
          console.log('📦 Message queued - will ACK when processed from queue');
        }
      }).catch((error) => {
        console.error('❌ Error in message handlers:', error);
        // Still ACK even on handler error to prevent message from being stuck
        console.log(`⚠️ ACKing message ${messageId} despite handler error`);
        this.ackMessage(messageId, subscriptionId);
      });

    } catch (error) {
      console.error('❌ Error parsing RabbitMQ message:', error);
      // ACK immediately on parse error since we can't process the message
      console.log(`⚠️ ACKing unparseable message ${messageId}`);
      this.ackMessage(messageId, subscriptionId);
    }
  }

  /**
   * Send ACK for received message
   */
  private ackMessage(messageId: string, subscriptionId?: string | null) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !subscriptionId) {
      console.warn('❌ Cannot ACK message: WebSocket not open');
      return;
    }

    const meta = this.subscriptionMeta.get(subscriptionId);

    if (!meta) return;

    this.sendStompCommand('ACK', {
      'id': messageId,
      'subscription': subscriptionId,
    });
  }

  public unsubscribeQueue(queueName: string = '', customerPhone: string = '') {
    queueName = queueName || this.getQueueName(customerPhone);
    const destination = `/queue/${queueName}`
    const subscription = this.subscriptions.get(destination);

    if (!subscription) {
      return;
    }

    subscription.refCount--;

    if (subscription.refCount > 0) {
      console.log(
        `📌 Still used by another screen: ${destination} (${subscription.refCount})`
      );

      return;
    }

    this.sendStompCommand('UNSUBSCRIBE', {
      id: subscription.subscriptionId,
    });

    this.subscriptionMeta.delete(subscription.subscriptionId);
    this.subscriptions.delete(destination);

    console.log(`❌ Unsubscribed: ${destination}`);

    // Không còn queue nào đang lắng nghe
    if (this.subscriptions.size === 0) {
      setTimeout(() => {
        if (this.subscriptions.size === 0) {
          this.disconnect();
        }
      }, 30000); // 30s
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000);

    this.reconnectTimer = setTimeout(() => {
      if (this.customerPhone) {
        console.log(`🔄 Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
        this.connect(this.customerPhone).catch((error) => {
          console.error('❌ Reconnection failed:', error);
        });
      }
    }, this.reconnectDelay);
  }

  /**
   * Handle incoming message asynchronously - returns Promise that resolves with shouldAck flag
   * Returns true if message was processed and should be ACKed, false if queued (will ACK later)
   */
  private async handleMessageAsync(message: RabbitMQMessage, messageId?: string, subscriptionId?: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        // This ensures messages always reach handlers for proper processing
        if (message.data.customerPhone && this.customerPhone) {
          // Normalize phones for comparison
          const normalizePhone = (phone: string) => phone.trim().replace(/\s+/g, '').toLowerCase();
          const messagePhone = normalizePhone(message.data.customerPhone);
          const currentPhone = normalizePhone(this.customerPhone);

          if (messagePhone !== currentPhone) {
            console.log('⚠️ Message phone mismatch at service level (but still sending to handlers):', {
              messagePhone,
              currentPhone
            });
          }
        }

        const handlers = this.messageHandlers.get(message.type);

        if (!handlers || handlers.size === 0) {
          // Queue message if no handlers are ready yet (don't ACK yet)
          if (messageId && subscriptionId) {
            pendingMessages.push({
              message,
              messageId,
              subscriptionId
            });

            // Try to process queued messages after a short delay
            setTimeout(() => {
              this.processPendingMessages();
            }, 100);
          } else {
            console.warn('⚠️ No handlers and no messageId/subscriptionId - cannot queue for ACK');
          }

          resolve(false);
          return;
        }

        const handlerPromises: Promise<void>[] = [];
        let handlerIndex = 0;

        handlers.forEach((handler) => {
          handlerIndex++;
          const currentIndex = handlerIndex;

          const handlerPromise = new Promise<void>((handlerResolve) => {
            try {
              handler(message);
              handlerResolve();

            } catch (error: any) {
              console.error(`❌ Error in handler ${currentIndex}:`, error);
              handlerResolve();
            }
          });

          handlerPromises.push(handlerPromise);
        });

        // Wait for all handlers to complete
        Promise.all(handlerPromises).then(() => {
          resolve(true);
        }).catch((error) => {
          console.error('❌ Error waiting for handlers:', error);
          resolve(true);
        });

      } catch (error) {
        console.error('❌ Error in handleMessageAsync:', error);
        resolve(true);
      }
    });
  }

  /**
   * Register a message handler
   */
  on(type: RabbitMQMessageType, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type)!.add(handler);

    // Process any pending messages when handler is registered
    setTimeout(() => {
      this.processPendingMessages();
    }, 10);

    // Return unsubscribe function
    return () => {
      const handlers = this.messageHandlers.get(type);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.messageHandlers.delete(type);
        }
      }
    };
  }

  /**
   * Update subscription to a new customer phone
   */
  updateSubscription(customerPhone: string) {
    if (this.customerPhone !== customerPhone) {
      this.unsubscribeQueue("", customerPhone);
      this.customerPhone = customerPhone;
      if (this.ws && this.ws.readyState === WebSocket.OPEN && this.isAuthenticated) {
        this.unsubscribeQueue("", customerPhone);
      }
    }
  }

  /**
   * Process pending messages that arrived before handlers were registered
   */
  private processPendingMessages() {
    if (pendingMessages.length === 0) return;

    // Process messages in order
    const processed: PendingMessage[] = [];

    while (pendingMessages.length > 0) {
      const pending = pendingMessages.shift()!;
      const { message, messageId, subscriptionId } = pending;
      const handlers = this.messageHandlers.get(message.type);

      if (handlers && handlers.size > 0) {

        // Process all handlers
        handlers.forEach((handler, index) => {
          try {
            handler(message);
          } catch (error) {
            console.error(`❌ Error in queued handler ${index}:`, error);
          }
        });

        this.ackMessage(messageId, subscriptionId);
        processed.push(pending);
      } else {
        pendingMessages.unshift(pending); // Put back at front
        // Retry after delay if still no handlers
        if (pendingMessages.length > 0) {
          setTimeout(() => {
            this.processPendingMessages();
          }, 500);
        }
        break; // Stop processing if no handlers available
      }
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(isManual: boolean = true) {
    console.log('🔌 Disconnecting...');

    // Stop heartbeat
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (isManual) {
      this.customerPhone = null;
      this.reconnectAttempts = 0;
      this.reconnectDelay = 3000;
      this.hasConnectedOnce = false;
      this.stopAutoReconnectForCurrentSession = false;
    }

    if (this.ws) {
      // this.unsubscribe(this.subscriptionId);
      for (const sub of Array.from(this.subscriptions.values())) {
        this.sendStompCommand("UNSUBSCRIBE", {
          id: sub.subscriptionId,
        });
      }

      // Send DISCONNECT frame
      if (this.ws.readyState === WebSocket.OPEN) {
        this.sendStompCommand('DISCONNECT');
      }

      this.ws.close();
      this.ws = null;
    }

    this.subscriptionMeta.clear();
    this.subscriptions.clear();

    this.isAuthenticated = false;
    this.isConnecting = false;

  }

  /**
   * Check if WebSocket is connected and authenticated
   */
  isConnected(): boolean {
    return this.ws !== null &&
      this.ws.readyState === WebSocket.OPEN &&
      this.isAuthenticated;
  }
}

// Singleton instance
export const rabbitMQWebSocketService = new RabbitMQWebSocketService();